import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import scrollama from "scrollama";
import { createDifferenceChart } from "./d3_visuals/drawDifferenceChart";
import { WeatherStrip } from "./WeatherStrip";
import { RecentAnomalyChart } from "./RecentAnomalyChart";
import { SeasonalShiftChart } from "./SeasonalShiftChart";
import "./styles.css";
import API_BASE from "../api";
import CityHero from "./CityMap";

// ── Data helpers ─────────────────────────────────────────────────────────────

// Returns "ascending" if today is before the annual peak of the 1951-80 baseline,
// "descending" if today is after it. Uses the full-year curve peak rather than a
// noisy 15-day comparison, which is too sensitive for coastal/unusual climates.
function detectSeasonalMode(mmddMap) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const todayMMDD = today.toISOString().slice(5, 10);

  const sorted = Array.from(mmddMap.entries())
    .map(([mmdd, avg]) => ({ mmdd, avg }))
    .sort((a, b) => a.mmdd.localeCompare(b.mmdd));

  if (!sorted.length) return "ascending";

  // Find the MM-DD where the baseline peaks across the full year
  const peakMMDD = sorted.reduce(
    (peak, d) => (d.avg > peak.avg ? d : peak),
    sorted[0]
  ).mmdd;

  return todayMMDD <= peakMMDD ? "ascending" : "descending";
}

// Find the crossover date on the 1951-80 curve for a given reference temperature.
// ascending: first date where baseline rises through refTemp (spring)
// descending: first date where baseline falls through refTemp (fall)
function findCrossover(seasonal, refTemp, mode, startMMDD = null) {
  // Only look at dates after today so we don't find crossovers that already passed
  // or ones in the wrong half of the year.
  const startIdx = startMMDD
    ? Math.max(1, seasonal.findIndex(d => d.mmdd >= startMMDD))
    : 1;

  if (mode === "ascending") {
    for (let i = startIdx; i < seasonal.length; i++) {
      if (seasonal[i - 1].avg < refTemp && seasonal[i].avg >= refTemp)
        return seasonal[i].mmdd;
    }
  } else {
    for (let i = startIdx; i < seasonal.length; i++) {
      if (seasonal[i - 1].avg > refTemp && seasonal[i].avg <= refTemp)
        return seasonal[i].mmdd;
    }
  }
  return null;
}

function buildCrossoverResult(crossoverMMDD, today, extra = {}) {
  const currentYear = today.getUTCFullYear();
  const crossoverDate = crossoverMMDD
    ? new Date(`${currentYear}-${crossoverMMDD}T00:00:00Z`)
    : null;
  const shiftDays = crossoverDate
    ? Math.round((crossoverDate - today) / (1000 * 60 * 60 * 24))
    : null;
  const absShift = shiftDays != null ? Math.abs(shiftDays) : null;
  return {
    ...extra,
    crossoverDate,
    shiftDays: absShift,
    // ascending: crossover in the future = arriving earlier
    // descending: crossover in the future = staying later
      isShifted: shiftDays != null && shiftDays > 0,
    crossoverMonthStr: crossoverDate
      ? crossoverDate.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        })
      : null,
  };
}

function computeSeasonalShift(entries) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  const observed = entries.filter((e) => e.actual != null && e.date <= today);
  if (observed.length < 30) return null;

  const last30 = observed.slice(-30);
  const avg30raw = last30.reduce((s, e) => s + e.actual, 0) / last30.length;

  const mmddMap = new Map();
  for (const e of entries) {
    if (e.baseline_avg == null) continue;
    const mmdd = e.date.toISOString().slice(5, 10);
    if (!mmddMap.has(mmdd)) mmddMap.set(mmdd, e.baseline_avg);
  }

  const seasonal = Array.from(mmddMap.entries())
    .map(([mmdd, avg]) => ({ mmdd, avg }))
    .sort((a, b) => a.mmdd.localeCompare(b.mmdd));

  const todayMMDD = today.toISOString().slice(5, 10);
  const mode = detectSeasonalMode(mmddMap);
  const crossoverMMDD = findCrossover(seasonal, avg30raw, mode, todayMMDD);

  const peakEntry = seasonal.reduce((a, b) => b.avg > a.avg ? b : a, seasonal[0]);
  const baselineMax = peakEntry.avg;
  const baselinePeakMMDD = peakEntry.mmdd;
  const baselinePeakDate = peakEntry ? new Date(`${today.getUTCFullYear()}-${baselinePeakMMDD}T00:00:00Z`) : null;
  const exceedsBaseline = avg30raw > baselineMax;

  const result = buildCrossoverResult(crossoverMMDD, today);
  return {
    avg30raw,
    avg30: avg30raw.toFixed(1),
    mode,
    exceedsBaseline,
    baselineMax,
    baselinePeakDate,
    ...result,
    isEarlier: mode === "ascending" && result.isShifted,
    isLater: mode === "descending" && result.isShifted,
  };
}

function computeBaselineShift(entries, recentBaselineRows) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const todayMMDD = today.toISOString().slice(5, 10);

  const todayRecent = recentBaselineRows.find((r) => r.month_day === todayMMDD);
  if (!todayRecent?.recent_avg) return null;
  const recentTodayTemp = todayRecent.recent_avg;

  const mmddMap = new Map();
  for (const e of entries) {
    if (e.baseline_avg == null) continue;
    const mmdd = e.date.toISOString().slice(5, 10);
    if (!mmddMap.has(mmdd)) mmddMap.set(mmdd, e.baseline_avg);
  }

  const seasonal = Array.from(mmddMap.entries())
    .map(([mmdd, avg]) => ({ mmdd, avg }))
    .sort((a, b) => a.mmdd.localeCompare(b.mmdd));

  const mode = detectSeasonalMode(mmddMap);
  const crossoverMMDD = findCrossover(seasonal, recentTodayTemp, mode, todayMMDD);

  const baselineMax = Math.max(...seasonal.map(d => d.avg));
  const exceedsBaseline = recentTodayTemp > baselineMax;

  const result = buildCrossoverResult(crossoverMMDD, today);
  return {
    recentTodayTemp,
    exceedsBaseline,
    mode,
    ...result,
    isEarlier: mode === "ascending" && result.isShifted,
    isLater: mode === "descending" && result.isShifted,
  };
}

function computeStats(entries, stationMeta) {
  const withActual = entries.filter((e) => e.actual != null);
  if (!withActual.length) return null;

  const last100 = withActual.filter((e) => e.actual_anomaly != null).slice(-100);
  const last100Above = last100.filter((e) => e.actual_anomaly > 0).length;

  const firstYear = stationMeta?.firstYear ?? entries[0].date.getUTCFullYear();
  const lastEntry = withActual[withActual.length - 1];
  const lastYear = stationMeta?.lastYear ?? lastEntry.date.getUTCFullYear();

  // Last 365 days
  const cutoff = new Date(lastEntry.date);
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 1);
  const recent = withActual.filter(
    (e) => e.date >= cutoff && e.actual_anomaly != null,
  );

  const pctAbove = recent.length
    ? Math.round(
        (recent.filter((e) => e.actual_anomaly > 0).length / recent.length) *
          100,
      )
    : 0;

  const avgAnomaly = recent.length
    ? recent.reduce((s, e) => s + e.actual_anomaly, 0) / recent.length
    : 0;

  return {
    firstYear,
    lastYear,
    yearsOfData: lastYear - firstYear + 1,
    pctAbove,
    avgAnomaly: Math.abs(avgAnomaly).toFixed(1),
    avgAnomalyPositive: avgAnomaly >= 0,
    lastActual: lastEntry.actual?.toFixed(1),
    lastAnomaly: lastEntry.actual_anomaly,
    lastAnomalyStr: `${lastEntry.actual_anomaly >= 0 ? "+" : ""}${lastEntry.actual_anomaly?.toFixed(1)}`,
    lastDateStr: lastEntry.date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }),
    lastMonthDay: lastEntry.date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }),
    baselineMax: lastEntry.baseline_max?.toFixed(1) ?? null,
    baselineMaxYear: lastEntry.baseline_max_year ?? null,
    lastEntry,
    last100Above,
    last100Total: last100.length,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = location.state || {};
  const [stats, setStats] = useState(null);
  const [unit, setUnit] = useState("F");
  const [activePanel, setActivePanel] = useState(0);
  const scrollerRef = useRef(null);
  const stepIndexRef = useRef(0);
  const [activeStep, setActiveStep] = useState(0);
  const debounceRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchActiveIndex, setSearchActiveIndex] = useState(-1);

  const city = data?.cityData?.city;
  const state = data?.cityData?.state;

  useEffect(() => {
    if (!data?.dailyMetricsData || !data?.recordTrendData?.length) return;

    // Convert object → sorted array (always Celsius — stats computed in °C)
    const _now = new Date();
    _now.setUTCHours(0, 0, 0, 0);
    const allEntries = Object.entries(data.dailyMetricsData)
      .map(([date, v]) => ({ date: new Date(date + "T00:00:00Z"), ...v }))
      .filter((e) => e.actual != null && e.date <= _now)
      .sort((a, b) => a.date - b.date);

    if (!allEntries.length) return;

    const trendEntries = data.recordTrendData
      .map((entry) => ({
        ...entry,
        date: new Date(`${entry.date}T00:00:00Z`),
      }))
      .sort((a, b) => a.date - b.date);

    if (!trendEntries.length) return;

    const computed = computeStats(allEntries, data?.stationMeta);
    const warmingRateC = trendEntries[0]?.warming_rate_per_decade ?? null;
    const baselineAvg = data?.baselineSummary?.baseline_period_avg ?? null;

    // Use pre-computed seasonal shift from viz_seasonal_shift pipeline table.
    // Fall back to client-side computation if the table isn't populated yet.
    const ss = data?.seasonalShift ?? null;
    const _cy = _now.getUTCFullYear();

    // Find today's 1951-80 baseline value from allEntries for cold/hot comparison
    const _todayMMDD = _now.toISOString().slice(5, 10);
    const _baselineTodayEntry = allEntries.find(
      (e) => e.date.toISOString().slice(5, 10) === _todayMMDD && e.baseline_avg != null
    );
    const _baselineTodayAvg = _baselineTodayEntry?.baseline_avg ?? null;

    const shift = ss
      ? {
          ...ss,
          avg30: ss.avg30raw,
          crossoverDate: ss.crossoverDate ? new Date(ss.crossoverDate + "T00:00:00Z") : null,
          baselinePeakDate: ss.baselinePeakMMDD
            ? new Date(`${_cy}-${ss.baselinePeakMMDD}T00:00:00Z`)
            : null,
          crossoverMonthStr: ss.crossoverDate
            ? new Date(ss.crossoverDate + "T00:00:00Z").toLocaleDateString("en-US", {
                month: "long", day: "numeric", timeZone: "UTC",
              })
            : null,
          baselineTodayAvg: _baselineTodayAvg,
          // >2°C below baseline = notably cold; >5°C = significantly cold
          isBelowBaseline: _baselineTodayAvg != null && ss.avg30raw != null && ss.avg30raw < _baselineTodayAvg - 2,
          isSignificantlyCold: _baselineTodayAvg != null && ss.avg30raw != null && ss.avg30raw < _baselineTodayAvg - 5,
        }
      : computeSeasonalShift(allEntries);
    const shiftWithDate = shift;

    const baselineShift = ss
      ? {
          recentTodayTemp: ss.recentTodayTemp,
          exceedsBaseline: ss.baselineExceeds,
          mode: ss.mode,
          crossoverDate: ss.baselineCrossoverDate ? new Date(ss.baselineCrossoverDate + "T00:00:00Z") : null,
          shiftDays: ss.baselineShiftDays,
          isShifted: ss.baselineShiftDays != null && ss.baselineShiftDays > 0,
          isEarlier: ss.baselineIsEarlier,
          isLater: ss.baselineIsLater,
          crossoverMonthStr: ss.baselineCrossoverDate
            ? new Date(ss.baselineCrossoverDate + "T00:00:00Z").toLocaleDateString("en-US", {
                month: "long", day: "numeric", timeZone: "UTC",
              })
            : null,
        }
      : computeBaselineShift(allEntries, data?.recentBaseline ?? []);

    setStats({ ...computed, shift: shiftWithDate, warmingRateC, baselineAvg, baselineShift });

    // Pre-convert to the selected unit before passing to D3
    const toU = (c) => (unit === "F" ? (c * 9) / 5 + 32 : c);
    const toDelta = (c) => (unit === "F" ? (c * 9) / 5 : c);

    const chartDailyData = allEntries.map((e) => ({
      ...e,
      actual: e.actual != null ? toU(e.actual) : null,
      baseline_avg: e.baseline_avg != null ? toU(e.baseline_avg) : null,
      actual_anomaly: e.actual_anomaly != null ? toDelta(e.actual_anomaly) : null,
    }));

    const chartTrendData = trendEntries.map((e) => ({
      ...e,
      actual: toU(e.actual),
      anomaly: e.anomaly != null ? toDelta(e.anomaly) : null,
      rolling_avg: e.rolling_avg != null ? toU(e.rolling_avg) : null,
      rolling_anomaly: e.rolling_anomaly != null ? toDelta(e.rolling_anomaly) : null,
      warming_rate_per_decade:
        e.warming_rate_per_decade != null
          ? toDelta(e.warming_rate_per_decade)
          : null,
    }));

    const recentBaselineData = (data?.recentBaseline ?? []).map((row) => ({
      month_day: row.month_day,
      recent_avg: row.recent_avg != null ? toU(row.recent_avg) : null,
    }));

    const chartApi = createDifferenceChart({
      containerId: "difference-chart",
      yearlyData: chartTrendData,
      dailyData: chartDailyData,
      recentBaselineData,
      unit,
    });
    if (!chartApi) return;

    const avg30Converted =
      shiftWithDate?.avg30raw != null ? toU(shiftWithDate.avg30raw) : undefined;

    const baselineShiftConverted = baselineShift
      ? {
          ...baselineShift,
          recentTodayTemp:
            baselineShift.recentTodayTemp != null
              ? toU(baselineShift.recentTodayTemp)
              : null,
        }
      : null;

    const steps = [
      () => chartApi.showIntro(),
      () => chartApi.showBaseline(),
      () => chartApi.showWarmingRate(),
      () => chartApi.show100DayBaseline(),
      () => chartApi.show100DayTwoBaselines(),
      () => chartApi.show100DayDifference(),
      () => chartApi.showBaselineComparison({ baselineShift: baselineShiftConverted }),
      () =>
        chartApi.showSeasonalShift({
          avg30: avg30Converted,
          crossoverDate: shiftWithDate?.crossoverDate,
          isEarlier: shiftWithDate?.isEarlier ?? false,
          isLater: shiftWithDate?.isLater ?? false,
          exceedsBaseline: shiftWithDate?.exceedsBaseline ?? false,
          baselineMax: shiftWithDate?.baselineMax != null ? toU(shiftWithDate.baselineMax) : null,
          baselinePeakDate: shiftWithDate?.baselinePeakDate ?? null,
        }),
    ];

    // Restore the step the user was on when unit changed
    steps[stepIndexRef.current]?.();

    const scroller = scrollama();
    scrollerRef.current = scroller;

    scroller
      .setup({ step: ".scroll-step", offset: 0.55, debug: false })
      .onStepEnter(({ index }) => {
        stepIndexRef.current = index;
        setActiveStep(index);
        steps[index]?.();
      });

    return () => {
      scroller.destroy();
    };
  }, [data, unit]);

  const loadCity = useCallback(async (cityData) => {
    try {
      window.scrollTo(0, 0);
      const dataResponse = await fetch(`${API_BASE}/get-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // nClimGrid backends use city_id; station backends use station_id.
        body: JSON.stringify({ city_id: cityData.city_id, station_id: cityData.station_id }),
      });
      if (!dataResponse.ok) return;
      const {
        daily_metrics, weather_strip, record_trend, baseline_summary,
        recent_baseline, past_100_days, seasonal_shift, station_meta,
      } = await dataResponse.json();
      navigate("/results", {
        state: {
          data: {
            cityData,
            dailyMetricsData: daily_metrics,
            weatherStripData: weather_strip,
            recordTrendData: record_trend,
            baselineSummary: baseline_summary,
            recentBaseline: recent_baseline,
            past100Days: past_100_days,
            seasonalShift: seasonal_shift,
            stationMeta: station_meta,
          },
        },
      });
    } catch {}
  }, [navigate]);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchSuggestions(await res.json());
        setSearchActiveIndex(-1);
      } catch { setSearchSuggestions([]); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleSearchSelect = (city) => {
    setSearchQuery("");
    setSearchSuggestions([]);
    loadCity(city);
  };

  const handleSearchKeyDown = (e) => {
    if (!searchSuggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSearchActiveIndex((i) => Math.min(i + 1, searchSuggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSearchActiveIndex((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (searchActiveIndex >= 0) handleSearchSelect(searchSuggestions[searchActiveIndex]); }
    else if (e.key === "Escape") setSearchSuggestions([]);
  };

  const displayTemp = (celsius) => {
    const n = parseFloat(celsius);
    return unit === "F" ? ((n * 9) / 5 + 32).toFixed(1) : n.toFixed(1);
  };

  const toColloquial = (celsius) => {
    const t = Math.round(parseFloat(displayTemp(celsius)));
    const decade = Math.floor(t / 10) * 10;
    const rem = ((t % 10) + 10) % 10;
    if (rem <= 2) return `low ${decade}s`;
    if (rem <= 6) return `mid-${decade}s`;
    return `upper ${decade}s`;
  };

  const displayDelta = (celsius) => {
    const n = parseFloat(celsius);
    return unit === "F" ? ((n * 9) / 5).toFixed(2) : n.toFixed(2);
  };

  if (!data) {
    return (
      <div className="no-data-message">
        No data available.{" "}
        <button onClick={() => navigate("/")}>Go back</button>
      </div>
    );
  }

  return (
    <div className="results-page">
      {/* Sticky top bar: site name (back to the landing page) and city search */}
      <div className="results-search-bar">
        <Link to="/" className="results-home-link" aria-label="How Much Hotter? home">
          <span aria-hidden="true">←</span> How Much Hotter?
        </Link>
        <div className="search-combobox">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search another city..."
            autoComplete="off"
            className="results-search-input"
          />
          {searchSuggestions.length > 0 && (
            <ul className="search-dropdown" role="listbox">
              {searchSuggestions.map((city, i) => (
                <li
                  key={`${city.zip ?? city.city_id}-${city.city}`}
                  role="option"
                  aria-selected={i === searchActiveIndex}
                  className={`search-option ${i === searchActiveIndex ? "search-option--active" : ""}`}
                  onMouseDown={() => handleSearchSelect(city)}
                >
                  <span className="search-option-city">{city.city}</span>
                  <span className="search-option-meta">
                    {city.state}{city.zip ? ` · ${city.zip}` : ""}
                    {city.zip_city && city.zip_city !== city.city ? ` (${city.zip_city})` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Header: city name, headline number, and city map (falls back to the
          centered header when there is no map, e.g. on station data) */}
      <CityHero
        cityId={data?.cityData?.city_id}
        city={city}
        state={state}
        unit={unit}
        weatherStrip={data?.weatherStripData}
        past100Days={data?.past100Days}
      />

      {/* Dashboard panel with tab toggle */}
      <div className="dashboard-panel">
        <div className="dashboard-controls">
          <div className="dashboard-tabs">
            {["Weather Strip", "Recent Trend", "Seasonal Shift"].map((label, i) => (
              <button
                key={i}
                className={`dashboard-tab ${activePanel === i ? "dashboard-tab--active" : ""}`}
                onClick={() => setActivePanel(i)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="unit-toggle">
            <button className={`unit-btn ${unit === "C" ? "unit-btn--active" : ""}`} onClick={() => setUnit("C")}>°C</button>
            <button className={`unit-btn ${unit === "F" ? "unit-btn--active" : ""}`} onClick={() => setUnit("F")}>°F</button>
          </div>
        </div>

        <div className="dashboard-panel-content">
          <div className={activePanel === 0 ? "dashboard-panel-active" : ""}>
            <WeatherStrip weatherStripData={data.weatherStripData} unit={unit} />
          </div>
          <div className={activePanel === 1 ? "dashboard-panel-active" : ""}>
            <RecentAnomalyChart dailyMetricsData={data.dailyMetricsData} unit={unit} />
          </div>
          <div className={activePanel === 2 ? "dashboard-panel-active" : ""}>
            <SeasonalShiftChart
              dailyMetricsData={data.dailyMetricsData}
              unit={unit}
              shiftOverride={stats?.shift ? {
                ...stats.shift,
                avg30: stats.shift.avg30raw != null
                  ? (unit === "F" ? (stats.shift.avg30raw * 9/5 + 32) : stats.shift.avg30raw)
                  : null,
                baselineTodayAvg: stats.shift.baselineTodayAvg != null
                  ? (unit === "F" ? (stats.shift.baselineTodayAvg * 9/5 + 32) : stats.shift.baselineTodayAvg)
                  : null,
              } : null}
            />
          </div>
        </div>
      </div>

      {/* Scrollytelling container */}
      <div className="scrolly-intro">
        <p className="scrolly-intro-eyebrow">The long view</p>
        <h2 className="scrolly-intro-heading">How did we get here?</h2>
        <p className="scrolly-intro-sub">Scroll to trace {city}'s temperature record from the historical baseline to today.</p>
      </div>
      <div className="scroll-container">
        {/* Sticky chart */}
        <div className="sticky-chart-wrapper">
          <div id="difference-chart" className="chart-canvas"></div>
          <div className="chart-legend" style={{ visibility: activeStep === 5 ? "visible" : "hidden" }}>
            <span className="legend-item legend-hot">▲ Above 1951–80 avg</span>
            <span className="legend-item legend-cool">▼ Below 1951–80 avg</span>
            <span className="legend-item legend-baseline">
              — Baseline curve
            </span>
          </div>
        </div>

        {/* Scroll steps */}
        <div className="scroll-steps">
          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE RECORD</p>
              <p className="step-text">
                {city} has <strong>{stats?.yearsOfData} years</strong> of daily
                temperature records, stretching back to{" "}
                <strong>{stats?.firstYear}</strong>. The line shows a
                trailing 5-year average of monthly highs, making the
                long-term trend easier to see.
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE BASELINE</p>
              <p className="step-text">
                The blue segment marks the 1951–1980 baseline — the reference
                for what "normal" looked like. The average daily high during
                those years was{" "}
                <strong className="temp-cool">
                  {stats?.baselineAvg != null
                    ? `${displayTemp(stats.baselineAvg)}°${unit}`
                    : "—"}
                </strong>
                .
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE WARMING RATE</p>
              <p className="step-text">
                Since 1951, {city} has warmed at roughly{" "}
                <strong className="temp-hot">
                  {stats?.warmingRateC != null
                    ? `+${displayDelta(stats.warmingRateC)}°${unit}`
                    : "—"}
                </strong>{" "}
                per decade. The trend line makes the direction clear.
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE PAST 100 DAYS</p>
              <p className="step-text">
                The curve shows the 1951–80 historical average for each of the
                past {stats?.last100Total ?? 100} days. This is what
                temperatures looked like before the major warming trend.
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE RAISED FLOOR</p>
              <p className="step-text">
                The blue line is the 2006–25 average — what a "normal" day looks
                like now. It runs consistently warmer than the 1951–80 baseline
                across all {stats?.last100Total ?? 100} days shown here.
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THE GAP</p>
              <p className="step-text">
                <strong className="temp-hot">{stats?.last100Above ?? "—"} of the past {stats?.last100Total ?? 100} days</strong>{" "}
                recorded a high above the historical baseline.{" "}
                The orange fill shows how far above — blue shows days that ran below.
              </p>
            </div>
          </div>

          <div className="scroll-step" id="scrolly-new-normal">
            <div className="step-card">
              <p className="step-label">THE NEW NORMAL</p>
              <p className="step-text">
                The solid line is the 2006–25 average — what "normal" looks
                like today. The dashed line is the 1951–80 baseline.
                {stats?.baselineShift?.isEarlier ? (
                  <>
                    {" "}
                    Temperatures typical for this time of year now weren't
                    typical until{" "}
                    <strong>{stats.baselineShift.crossoverMonthStr}</strong>{" "}
                    historically —{" "}
                    <strong className="temp-hot">
                      {stats.baselineShift.shiftDays} days
                    </strong>{" "}
                    later in the season. The seasonal clock has shifted forward.
                  </>
                ) : stats?.baselineShift?.isLater ? (
                  <>
                    {" "}
                    Temperatures like these historically dropped off by{" "}
                    <strong>{stats.baselineShift.crossoverMonthStr}</strong>, but
                    the new normal stays warm{" "}
                    <strong className="temp-hot">
                      {stats.baselineShift.shiftDays} days
                    </strong>{" "}
                    longer into the season.
                  </>
                ) : stats?.baselineShift?.exceedsBaseline ? (
                  <>
                    {" "}
                    Today's average of{" "}
                    <strong className="temp-hot">
                      {displayTemp(stats.baselineShift.recentTodayTemp)}°{unit}
                    </strong>{" "}
                    is warmer than temperatures reached at any point in the 1951–80 baseline year.
                    The seasonal curve has shifted entirely beyond its historical range.
                  </>
                ) : (
                  <>
                    {" "}
                    The seasonal curve has shifted upward across the board.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="scroll-step">
            <div className="step-card">
              <p className="step-label">THIS YEAR</p>
              <p className="step-text">
                The past 30 days averaged{" "}
                <strong className="temp-hot">
                  {displayTemp(stats?.shift?.avg30)}°{unit}
                </strong>.
                {stats?.shift?.isEarlier ? (
                  <>
                    {" "}
                    In the 1951–80 baseline, temperatures like these weren't
                    typical until{" "}
                    <strong>{stats.shift.crossoverMonthStr}</strong> —{" "}
                    <strong className="temp-hot">
                      {stats.shift.shiftDays} days
                    </strong>{" "}
                    later than today.{" "}
                    {stats?.shift?.baselineTodayAvg != null &&
                      stats.shift.avg30raw - stats.shift.baselineTodayAvg > 2.2 && (
                      <>
                        This time of year used to average in the{" "}
                        <strong className="temp-cool">
                          {toColloquial(stats.shift.baselineTodayAvg)}
                        </strong>.{" "}
                      </>
                    )}
                    {stats?.baselineShift?.isEarlier && stats.shift.shiftDays > stats.baselineShift.shiftDays ? (
                      <>This year is running ahead of even the new normal.</>
                    ) : stats?.baselineShift?.isEarlier ? (
                      <>This year is within the range of the new normal.</>
                    ) : (
                      <>Summer is arriving earlier.</>
                    )}
                  </>
                ) : stats?.shift?.isLater ? (
                  <>
                    {" "}
                    In the 1951–80 baseline, temperatures like these were last
                    typical on <strong>{stats.shift.crossoverMonthStr}</strong> —{" "}
                    <strong className="temp-hot">
                      {stats.shift.shiftDays} days
                    </strong>{" "}
                    ago.{" "}
                    {stats?.shift?.baselineTodayAvg != null &&
                      stats.shift.avg30raw - stats.shift.baselineTodayAvg > 2.2 && (
                      <>
                        This time of year used to average in the{" "}
                        <strong className="temp-cool">
                          {toColloquial(stats.shift.baselineTodayAvg)}
                        </strong>.{" "}
                      </>
                    )}
                    {stats?.baselineShift?.isLater && stats.shift.shiftDays > stats.baselineShift.shiftDays ? (
                      <>
                        Summer warmth is lingering even longer than under the new normal
                        ({stats.baselineShift.shiftDays} days).
                      </>
                    ) : stats?.baselineShift?.isLater ? (
                      <>That's within the range of the new normal.</>
                    ) : (
                      <>Summer warmth is lingering.</>
                    )}
                  </>
                ) : stats?.shift?.exceedsBaseline ? (
                  <>
                    {" "}
                    {stats?.shift?.baselineTodayAvg != null &&
                    stats.shift.avg30raw - stats.shift.baselineTodayAvg > 2.2 ? (
                      <>
                        A day like this used to average in the{" "}
                        <strong className="temp-cool">
                          {toColloquial(stats.shift.baselineTodayAvg)}
                        </strong>{" "}
                        on this date — today's temperatures are beyond anything in the 1951–80 baseline record.
                      </>
                    ) : (
                      <>That's warmer than temperatures at any point in the 1951–80 baseline record.</>
                    )}
                  </>
                ) : stats?.shift?.isSignificantlyCold ? (
                  <>
                    {" "}
                    That's well below the 1951–80 historical average of{" "}
                    <strong className="temp-cold">
                      {displayTemp(stats.shift.baselineTodayAvg)}°{unit}
                    </strong>{" "}
                    for this time of year — an unusually cold stretch.
                  </>
                ) : stats?.shift?.isBelowBaseline ? (
                  <>
                    {" "}
                    That's below the 1951–80 historical average of{" "}
                    <strong>
                      {displayTemp(stats.shift.baselineTodayAvg)}°{unit}
                    </strong>{" "}
                    for this time of year.
                  </>
                ) : (
                  <>
                    {" "}
                    Temperatures like these are within the typical range for
                    this time of year historically.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="results-footer">
        <p className="results-source">
          Data: NOAA GHCN daily temperature records. Compared against 1951–1980
          baseline average with 15-day rolling smoothing.
        </p>
      </div>
    </div>
  );
}

export default ResultsPage;
